import React, { useState, useEffect } from 'react';
import { MenuItem, Select, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { TogglePage } from '../../dashboard/ui/TogglePage';
import CloseIcon from "@mui/icons-material/Close";
import { GRADIENTS } from '../../ui/styles/gradients';
import { getSortOptions, getStatusOptions, getExtensionStatusOptions } from '../../../utils/common/videoFilterOptions';

interface User {
  email: string;
  count: number;
  role: string;
}

type SortOption = 'recent' | 'oldest' | 'largest' | 'smallest' | 'user_asc' | 'user_desc';

interface VideoFiltersProps {
  isAdmin: boolean;
  sortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  selectedUser: string;
  onUserChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  allUsers?: User[];
  isLoadingUsers: boolean;
  pageValue?: string;
  setPageValue?: React.Dispatch<React.SetStateAction<'Videos' | 'Images' | 'ExtensionRequests'>>;
  showUserFilter?: boolean; // Option to force show user filter regardless of admin status
  hideSortBy?: boolean; // Hide the sort-by control
}

const VideoFilters: React.FC<VideoFiltersProps> = ({
  isAdmin,
  sortBy,
  onSortChange,
  selectedUser,
  onUserChange,
  selectedStatus,
  onStatusChange,
  allUsers = [],
  isLoadingUsers,
  pageValue = 'Videos',
  setPageValue,
  showUserFilter = false,
  hideSortBy = false,
}) => {
  // Search state for user filter
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSortOrder, setUserSortOrder] = useState<'desc' | 'asc'>('desc'); // Default: most videos first
  
  // Filter sort options based on user role
  const sortOptions = getSortOptions();
  const statusOptions = pageValue === 'ExtensionRequests'
    ? getExtensionStatusOptions()
    : getStatusOptions();
  
  // Filter and sort users based on search term and sort order
  const filteredUsers = Array.isArray(allUsers) 
    ? allUsers
        .filter(user => 
          user.count > 0 && 
          user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
        )
        .sort((a, b) => {
          if (userSortOrder === 'desc') {
            return b.count - a.count; // Most videos first
          } else {
            return a.count - b.count; // Least videos first
          }
        })
    : [];
  
  return (
    <div className="mb-8 px-4 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        {/* Left side - Filter Controls */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Sort By */}
          {!hideSortBy && (
          <div className="flex flex-col">
            <label htmlFor="sort-select" className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
              Sort by
            </label>
            <Select
              id="sort-select"
              value={sortBy}
              size="small"
              onChange={(e) => onSortChange(e.target.value as SortOption)}
              sx={{
                borderRadius: '10px',
                fontSize: '14px',
                width: '200px',
              }}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
          )}

          {/* Filter by User - Admin or content-review page */}
          {(isAdmin || showUserFilter) && (
            <div className="flex flex-col">
              <label htmlFor="user-select" className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
                Filter by User
              </label>
              <Select
                id="user-select"
                value={selectedUser}
                size="small"
                onChange={(e) => onUserChange(e.target.value)}
                onClose={() => setUserSearchTerm('')}
                sx={{
                  borderRadius: '10px',
                  fontSize: '14px',
                  width: '250px',
                }}
                disabled={isLoadingUsers}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 400,
                    },
                  },
                }}
              >
                {/* Search and Sort field */}
                <MenuItem 
                  disableRipple 
                  onKeyDown={(e) => e.stopPropagation()}
                  sx={{ 
                    '&:hover': { backgroundColor: 'transparent' },
                    cursor: 'default',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    display: 'block',
                  }}
                >
                  <div style={{ width: '100%' }}>
                    {/* Header with sort info */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: '#6B7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Search & Sort
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 8px',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: '#374151',
                          backgroundColor: '#F3F4F6',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                      >
                        {userSortOrder === 'desc' ? (
                          <>
                            <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                            <span>Most first</span>
                          </>
                        ) : (
                          <>
                            <ArrowUpwardIcon sx={{ fontSize: '14px' }} />
                            <span>Least first</span>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* Search field */}
                    <TextField
                      size="small"
                      placeholder="Search users..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      fullWidth
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                        },
                      }}
                    />
                  </div>
                </MenuItem>
                
                <MenuItem value="all">All Users</MenuItem>
                
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      {user.email} ({user.count})
                    </MenuItem>
                  ))
                ) : (
                  userSearchTerm && (
                    <MenuItem disabled>
                      No users found
                    </MenuItem>
                  )
                )}
              </Select>
            </div>
          )}

          {/* Filter by Status */}
          <div className="flex flex-col">
            <label htmlFor="status-select" className="block text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">
              Filter by Status
            </label>
            <Select
              id="status-select"
              value={selectedStatus}
              size="small"
              onChange={(e) => onStatusChange(e.target.value)}
              sx={{
                borderRadius: '10px',
                fontSize: '14px',
                width: '200px',
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {/* Right side - Tab toggle */}
        {setPageValue && (
          <div className='flex items-center'>
            <TogglePage 
              value={pageValue} 
              handleChange={(_event: React.MouseEvent<HTMLElement>, newValue: string | null) => {
                if (newValue && setPageValue) {
                  setPageValue(newValue as 'Videos' | 'Images' | 'ExtensionRequests');
                }
              }}
              showTranscripts={false}
              showExtensionRequests={isAdmin}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile Video Filter Component
interface MobileVideoFilterProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  sortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  selectedUser: string;
  onUserChange: (value: string) => void;
  selectedStatus: string;
  onStatusChange: (value: string) => void;
  allUsers?: User[];
  isLoadingUsers: boolean;
}

const MobileVideoFilter: React.FC<MobileVideoFilterProps> = ({
  isOpen,
  onClose,
  isAdmin,
  sortBy,
  onSortChange,
  selectedUser,
  onUserChange,
  selectedStatus,
  onStatusChange,
  allUsers = [],
  isLoadingUsers,
}) => {
  const [tempSortBy, setTempSortBy] = useState(sortBy);
  const [tempSelectedUser, setTempSelectedUser] = useState(selectedUser);
  const [tempSelectedStatus, setTempSelectedStatus] = useState(selectedStatus);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSortOrder, setUserSortOrder] = useState<'desc' | 'asc'>('desc'); // Default: most videos first

  // Update temp values when props change
  useEffect(() => {
    setTempSortBy(sortBy);
    setTempSelectedUser(selectedUser);
    setTempSelectedStatus(selectedStatus);
  }, [sortBy, selectedUser, selectedStatus]);
  
  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUserSearchTerm('');
    }
  }, [isOpen]);

  // Filter sort options based on user role
  const sortOptions = getSortOptions();
  const statusOptions = getStatusOptions();
  
  // Filter and sort users based on search term and sort order
  const filteredUsers = Array.isArray(allUsers) 
    ? allUsers
        .filter(user => 
          user.count > 0 && 
          user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
        )
        .sort((a, b) => {
          if (userSortOrder === 'desc') {
            return b.count - a.count; // Most videos first
          } else {
            return a.count - b.count; // Least videos first
          }
        })
    : [];

  const handleApply = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    // Batch all filter changes into single calls to avoid stale state
    // Call handlers in sequence - React will batch these updates
    onSortChange(tempSortBy);
    onUserChange(tempSelectedUser);
    onStatusChange(tempSelectedStatus);
    
    // Close modal
    onClose();
  };

  const handleReset = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setTempSortBy('recent');
    setTempSelectedUser('all');
    setTempSelectedStatus('all');
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    // Reset temp values to current values when closing without applying
    setTempSortBy(sortBy);
    setTempSelectedUser(selectedUser);
    setTempSelectedStatus(selectedStatus);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .scrollbar-hidden::-webkit-scrollbar {
          display: none;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }
      `}</style>
      <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Background Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity z-0"
        onClick={(e) => handleClose(e)}
        aria-hidden="true"
      />
      
      {/* Drawer Content */}
      <dialog 
        open
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl transform translate-y-0 transition-transform duration-300 ease-out animate-in slide-in-from-bottom max-h-[80vh] flex flex-col overflow-hidden z-10 border-0 m-0 p-0 w-full"
        aria-labelledby="filter-modal-title"
      >
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0">
          <h2 id="filter-modal-title" className="text-lg font-semibold text-gray-900">Filter & Sort</h2>
          <button
            type="button"
            onClick={(e) => handleClose(e)}
            className="p-2 rounded-full transition-colors bg-white focus:outline-none"
          >
            <CloseIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div 
          className="flex-1 overflow-y-auto px-6 pb-4 scrollbar-hidden"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
          }}
        >
        {/* Sort By Section */}
        <div className="mb-6">
          <h3 className="text-base text-left font-medium text-gray-900 mb-4">Sort By</h3>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTempSortBy(option.value as SortOption)}
                className={`px-4 py-2 rounded-full border-2 transition-all duration-200 ${
                  tempSortBy === option.value
                    ? 'text-white border-transparent hover:opacity-90'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                style={tempSortBy === option.value ? {
                  background: GRADIENTS.orangeToPurple,
                } : undefined}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Status Section */}
        <div className="mb-6">
          <h3 className="text-base text-left font-medium text-gray-900 mb-4">Status</h3>
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setTempSelectedStatus(option.value)}
                className={`px-4 py-2 rounded-full border-2 transition-all duration-200 ${
                  tempSelectedStatus === option.value
                    ? 'text-white border-transparent hover:opacity-90'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                style={tempSelectedStatus === option.value ? {
                  background: GRADIENTS.orangeToPurple,
                } : undefined}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* User Section - Admin only */}
        {isAdmin && (
          <div className="mb-8">
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <h3 className="text-base text-left font-medium text-gray-900 m-0">User</h3>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setUserSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors"
                style={{
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                {userSortOrder === 'desc' ? (
                  <>
                    <ArrowDownwardIcon fontSize="small" style={{ fontSize: '16px' }} />
                    <span>Most first</span>
                  </>
                ) : (
                  <>
                    <ArrowUpwardIcon fontSize="small" style={{ fontSize: '16px' }} />
                    <span>Least first</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Search input */}
            <div className="mb-3">
              <TextField
                size="small"
                placeholder="Search users..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                fullWidth
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
            </div>
            
            <div 
              className="space-y-3 max-h-60 overflow-y-auto custom-scrollbar"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#D1D5DB #F3F4F6'
              }}
            >
              {/* All Users option */}
              <button
                type="button"
                aria-label="Select all users"
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer w-full text-left bg-white"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setTempSelectedUser('all');
                }}
              >
                <div className="flex items-center">
                  <div 
                    className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${
                      tempSelectedUser === 'all' ? 'border-transparent' : 'border-gray-300'
                    }`}
                    style={tempSelectedUser === 'all' ? {
                      background: GRADIENTS.orangeToPurple,
                    } : undefined}
                  >
                    {tempSelectedUser === 'all' && (
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="text-gray-700">All</span>
                </div>
              </button>
              
              {/* Individual users */}
              {!isLoadingUsers && filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <button
                    key={user.email}
                    type="button"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer w-full text-left bg-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setTempSelectedUser(user.email);
                    }}
                  >
                    <div className="flex items-center">
                      <div 
                        className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${
                          tempSelectedUser === user.email ? 'border-transparent' : 'border-gray-300'
                        }`}
                        style={tempSelectedUser === user.email ? {
                          background: GRADIENTS.orangeToPurple,
                        } : undefined}
                      >
                        {tempSelectedUser === user.email && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-gray-700">{user.email.split('@')[0]}</span>
                    </div>
                    <span className="text-gray-500 text-sm">{user.count}</span>
                  </button>
                ))
              ) : (
                !isLoadingUsers && userSearchTerm && (
                  <div className="p-3 text-center text-gray-500 text-sm">
                    No users found
                  </div>
                )
              )}
            </div>
          </div>
        )}

        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex gap-3 p-6 pt-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={(e) => handleReset(e)}
            className="flex-1 py-3 px-6 border border-black text-gray-700 font-medium rounded-full bg-white transition-colors hover:bg-gray-50"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={(e) => handleApply(e)}
            className="flex-1 py-3 px-6 bg-gray-900 text-white font-medium rounded-full hover:bg-gray-800 transition-colors"
          >
            Apply
          </button>
        </div>
      </dialog>
      </div>
    </>
  );
};

// Filter Icon Component
export const FilterIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      aria-label="Open filters"
      className="transition-colors hover:opacity-80 focus:outline-none"
      style={{ 
        border: 'none', 
        background: 'white', 
        padding: 0,
        outline: 'none',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.09)',
        borderRadius: '32px',
        height: '34px',
        width: '64px',
      }}
    >
      <svg width="64" height="34" viewBox="0 0 64 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect 
          width="64" 
          height="34" 
          rx="17" 
          fill="white" 
        />
        <path d="M31 8H11L19 17.46V24L23 26V17.46L31 8Z" stroke="#393939" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M44.5 15.1113L51.5 15.1113L48 20.3058L44.5 15.1113Z" fill="#393939"/>
      </svg>
    </button>
  );
};

export { MobileVideoFilter, VideoFilters };
